/**
 * GlobalApiErrorBanner
 *
 * Sticky top banner that appears when useApiHealthStore.apiDown = true.
 * Non-blocking — user can still see the page and click Reload.
 * Automatically dismisses when the API recovers.
 */
import React, { useEffect, useState } from 'react';
import { useApiHealthStore } from '@/zusttandStore/apiHealth.store';

const GlobalApiErrorBanner: React.FC = () => {
  const { apiDown, lastError } = useApiHealthStore();
  const [visible, setVisible] = useState(false);

  // Delayed show — avoid flash for momentary blips
  useEffect(() => {
    if (apiDown) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [apiDown]);

  if (!visible) return null;

  const isOffline = lastError === 'network' || !navigator.onLine;

  return (
    <div
      role='alert'
      aria-live='assertive'
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99998,
        background: isOffline
          ? 'linear-gradient(90deg, #722ed1, #9254de)'
          : 'linear-gradient(90deg, #cf1322, #ff4d4f)',
        color: '#fff',
        padding: '9px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 13,
        fontWeight: 500,
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
        animation: 'banner-slide-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        fontFamily:
          "'IBM Plex Sans Thai', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>{isOffline ? '📡' : '⚠️'}</span>
        <span>
          {isOffline
            ? 'ไม่มีการเชื่อมต่ออินเทอร์เน็ต · No internet connection'
            : 'ไม่สามารถเชื่อมต่อระบบได้ กำลังลองใหม่... · Server unreachable, retrying…'}
        </span>
      </div>

      <button
        onClick={() => window.location.reload()}
        style={{
          flexShrink: 0,
          background: 'rgba(255,255,255,0.2)',
          border: '1px solid rgba(255,255,255,0.45)',
          borderRadius: 6,
          color: '#fff',
          padding: '3px 12px',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          transition: 'background 0.15s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.35)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)';
        }}
      >
        Reload
      </button>

      <style>{`
        @keyframes banner-slide-in {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default GlobalApiErrorBanner;
